import React, {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const EDGE_GAP = 10;

type TooltipPosition = "top" | "bottom" | "left" | "right";

interface SpaceMetrics {
  spaceTop: number;
  spaceBottom: number;
  spaceLeft: number;
  spaceRight: number;
}

interface PositionGeometry extends SpaceMetrics {
  triggerRect: DOMRect;
  triggerCenter: number;
  triggerMiddle: number;
  tooltipWidth: number;
  tooltipHeight: number;
  viewportWidth: number;
  viewportHeight: number;
}

interface DetermineBestPositionParams extends SpaceMetrics {
  tooltipWidth: number;
  tooltipHeight: number;
  wouldOverflowLeft: boolean;
  wouldOverflowRight: boolean;
}

interface Coordinates {
  top: number;
  left: number;
}

interface ArrowOffset {
  offset: string;
  type: "horizontal" | "vertical" | "center";
}

interface TooltipProps {
  children: ReactNode;
  text: ReactNode;
  className?: string;
  alwaysVisible?: boolean;
  disablePad?: boolean;
  subContainerClassName?: string;
}

const determineFallbackPosition = ({
  spaceTop,
  spaceBottom,
  spaceLeft,
  spaceRight,
  tooltipHeight,
}: SpaceMetrics & { tooltipHeight: number }): TooltipPosition => {
  if (spaceTop > tooltipHeight || spaceBottom > tooltipHeight) {
    /* istanbul ignore next */
    return spaceTop > spaceBottom ? "top" : "bottom";
  }
  /* istanbul ignore next */
  return spaceLeft > spaceRight ? "left" : "right";
};

const determineBestPosition = ({
  spaceTop,
  spaceBottom,
  spaceLeft,
  spaceRight,
  tooltipWidth,
  tooltipHeight,
  wouldOverflowLeft,
  wouldOverflowRight,
}: DetermineBestPositionParams): TooltipPosition => {
  const fitsVertically = !wouldOverflowLeft && !wouldOverflowRight;

  if (spaceTop >= tooltipHeight + EDGE_GAP && fitsVertically) return "top";
  if (spaceBottom >= tooltipHeight + EDGE_GAP && fitsVertically)
    return "bottom";
  if (wouldOverflowRight && spaceLeft >= tooltipWidth + EDGE_GAP) return "left";
  if (wouldOverflowLeft && spaceRight >= tooltipWidth + EDGE_GAP)
    return "right";
  if (spaceLeft >= tooltipWidth + EDGE_GAP) return "left";
  if (spaceRight >= tooltipWidth + EDGE_GAP) return "right";

  return determineFallbackPosition({
    spaceTop,
    spaceBottom,
    spaceLeft,
    spaceRight,
    tooltipHeight,
  });
};

const calculateCoordinates = (
  bestPosition: TooltipPosition,
  {
    triggerRect,
    triggerCenter,
    triggerMiddle,
    tooltipWidth,
    tooltipHeight,
  }: Pick<
    PositionGeometry,
    | "triggerRect"
    | "triggerCenter"
    | "triggerMiddle"
    | "tooltipWidth"
    | "tooltipHeight"
  >,
): Coordinates => {
  switch (bestPosition) {
    case "bottom":
      return {
        top: triggerRect.bottom + 8,
        left: triggerCenter - tooltipWidth / 2,
      };
    case "left":
      return {
        top: triggerMiddle - tooltipHeight / 2,
        left: triggerRect.left - tooltipWidth - 8,
      };
    case "right":
      return {
        top: triggerMiddle - tooltipHeight / 2,
        left: triggerRect.right + 8,
      };
    case "top":
      return {
        top: triggerRect.top - tooltipHeight - 8,
        left: triggerCenter - tooltipWidth / 2,
      };
    /* istanbul ignore next */
    default:
      return {
        top: triggerRect.top - tooltipHeight - 8,
        left: triggerCenter - tooltipWidth / 2,
      };
  }
};

const clampToViewport = (
  { top, left }: Coordinates,
  {
    tooltipWidth,
    tooltipHeight,
    viewportWidth,
    viewportHeight,
  }: Pick<
    PositionGeometry,
    "tooltipWidth" | "tooltipHeight" | "viewportWidth" | "viewportHeight"
  >,
): Coordinates => {
  let clampedLeft = left;
  let clampedTop = top;

  if (clampedLeft < EDGE_GAP) clampedLeft = EDGE_GAP;
  if (clampedLeft + tooltipWidth > viewportWidth - EDGE_GAP) {
    clampedLeft = viewportWidth - tooltipWidth - EDGE_GAP;
  }
  if (clampedTop < EDGE_GAP) clampedTop = EDGE_GAP;
  if (clampedTop + tooltipHeight > viewportHeight - EDGE_GAP) {
    clampedTop = viewportHeight - tooltipHeight - EDGE_GAP;
  }

  return { top: clampedTop, left: clampedLeft };
};

const calculateArrowOffset = (
  bestPosition: TooltipPosition,
  {
    left,
    top,
    triggerCenter,
    triggerMiddle,
    tooltipWidth,
    tooltipHeight,
  }: Coordinates &
    Pick<
      PositionGeometry,
      "triggerCenter" | "triggerMiddle" | "tooltipWidth" | "tooltipHeight"
    >,
): ArrowOffset => {
  if (bestPosition === "top" || bestPosition === "bottom") {
    const triggerCenterRelativeToTooltip = triggerCenter - left;
    const arrowOffsetPx = Math.max(
      20,
      Math.min(triggerCenterRelativeToTooltip, tooltipWidth - 20),
    );
    return { offset: `${arrowOffsetPx}px`, type: "horizontal" };
  }

  const triggerMiddleRelativeToTooltip = triggerMiddle - top;
  const arrowOffsetPx = Math.max(
    20,
    Math.min(triggerMiddleRelativeToTooltip, tooltipHeight - 20),
  );
  return { offset: `${arrowOffsetPx}px`, type: "vertical" };
};

const Tooltip = ({
  children,
  text,
  className = "",
  alwaysVisible = false,
  disablePad = false,
  subContainerClassName = "",
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(alwaysVisible);
  const [position, setPosition] = useState<TooltipPosition>("top");
  const [tooltipPosition, setTooltipPosition] = useState<Coordinates>({
    top: 0,
    left: 0,
  });
  const [arrowOffset, setArrowOffset] = useState<ArrowOffset>({
    offset: "50%",
    type: "center",
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimeRef = useRef<number | null>(null);
  const touchHandledRef = useRef(false);

  const calculatePosition = () => {
    /* istanbul ignore if */
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceTop = triggerRect.top;
    const spaceBottom = viewportHeight - triggerRect.bottom;
    const spaceLeft = triggerRect.left;
    const spaceRight = viewportWidth - triggerRect.right;

    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;

    // Calculate center position of trigger element
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const triggerMiddle = triggerRect.top + triggerRect.height / 2;

    // Check if tooltip would overflow horizontally when centered
    const tooltipHalfWidth = tooltipWidth / 2;
    const wouldOverflowLeft = triggerCenter - tooltipHalfWidth < EDGE_GAP;
    const wouldOverflowRight =
      triggerCenter + tooltipHalfWidth > viewportWidth - EDGE_GAP;

    const geometry: PositionGeometry = {
      triggerRect,
      triggerCenter,
      triggerMiddle,
      tooltipWidth,
      tooltipHeight,
      viewportWidth,
      viewportHeight,
      spaceTop,
      spaceBottom,
      spaceLeft,
      spaceRight,
    };

    const bestPosition = determineBestPosition({
      spaceTop,
      spaceBottom,
      spaceLeft,
      spaceRight,
      tooltipWidth,
      tooltipHeight,
      wouldOverflowLeft,
      wouldOverflowRight,
    });

    const { top, left } = clampToViewport(
      calculateCoordinates(bestPosition, geometry),
      geometry,
    );

    const arrowOffsetValue = calculateArrowOffset(bestPosition, {
      left,
      top,
      triggerCenter,
      triggerMiddle,
      tooltipWidth,
      tooltipHeight,
    });

    setPosition(bestPosition);
    setTooltipPosition({ top, left });
    setArrowOffset(arrowOffsetValue);
  };

  useLayoutEffect(() => {
    if (
      (isVisible || alwaysVisible) &&
      triggerRef.current &&
      tooltipRef.current
    ) {
      calculatePosition();

      const observer = new ResizeObserver(() => {
        calculatePosition();
      });
      observer.observe(tooltipRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [isVisible, alwaysVisible]);

  // Recalculate position on scroll or resize
  useEffect(() => {
    if (isVisible || alwaysVisible) {
      const handleScrollOrResize = () => {
        /* istanbul ignore if */
        if (!triggerRef.current || !tooltipRef.current) return;
        calculatePosition();
      };

      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);

      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
  }, [isVisible, alwaysVisible]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (alwaysVisible) return;
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    if (alwaysVisible) return;
    // Delay hiding to allow mouse to move to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  const handleTooltipMouseEnter = () => {
    if (alwaysVisible) return;
    // Cancel hide when mouse enters tooltip
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    if (alwaysVisible) return;
    // Hide immediately when leaving tooltip
    setIsVisible(false);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (alwaysVisible) return;

    // If we just handled a touch event, ignore the click
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.stopPropagation();
    e.preventDefault();
    toggleTooltip();
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (alwaysVisible) return;

    // Mark that we handled a touch event
    touchHandledRef.current = true;

    // Reset the flag after a short delay (to catch the subsequent click)
    setTimeout(() => {
      touchHandledRef.current = false;
    }, 500);

    e.stopPropagation();
    e.preventDefault();
    toggleTooltip();
  };

  const toggleTooltip = () => {
    const newVisibleState = !isVisible;
    setIsVisible(newVisibleState);

    // Track when we open the tooltip with a timestamp
    if (newVisibleState) {
      openTimeRef.current = Date.now();
    }
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      // Ignore events that happen within 300ms of opening (mobile tap issue)
      // This prevents the touchstart/click event from immediately closing the tooltip
      /* istanbul ignore if */
      if (openTimeRef.current && Date.now() - openTimeRef.current < 300) return;

      if (
        triggerRef.current &&
        e.target instanceof Node &&
        !triggerRef.current.contains(e.target) &&
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible && !alwaysVisible) {
      // Use capture phase to catch events before they bubble
      // Use mousedown instead of click for better responsiveness
      // Use touchend instead of touchstart to avoid conflicts with the opening tap
      document.addEventListener("mousedown", handleClickOutside, true);
      document.addEventListener("touchend", handleClickOutside, true);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside, true);
        document.removeEventListener("touchend", handleClickOutside, true);
      };
    }
  }, [isVisible, alwaysVisible]);

  const getArrowClasses = (): string => {
    switch (position) {
      case "top":
        return "top-full border-l-transparent border-r-transparent border-b-transparent";
      case "bottom":
        return "bottom-full border-l-transparent border-r-transparent border-t-transparent";
      case "left":
        return "left-full border-t-transparent border-b-transparent border-r-transparent";
      case "right":
        return "right-full border-t-transparent border-b-transparent border-l-transparent";
      /* istanbul ignore next */
      default:
        return "top-full border-l-transparent border-r-transparent border-b-transparent";
    }
  };

  const getArrowBorderColor = (): CSSProperties => {
    const arrowColor = "#dee7ff";
    switch (position) {
      case "top":
        return { borderTopColor: arrowColor };
      case "bottom":
        return { borderBottomColor: arrowColor };
      case "left":
        return { borderLeftColor: arrowColor };
      case "right":
        return { borderRightColor: arrowColor };
      /* istanbul ignore next */
      default:
        return { borderTopColor: arrowColor };
    }
  };

  const getArrowStyle = (): CSSProperties => {
    const baseStyle: CSSProperties = {
      width: 0,
      height: 0,
      borderWidth: "8px",
      borderStyle: "solid",
      ...getArrowBorderColor(),
    };

    if (position === "top" || position === "bottom") {
      return {
        ...baseStyle,
        left: arrowOffset.offset,
        transform: "translateX(-50%)",
      };
    } else {
      return {
        ...baseStyle,
        top: arrowOffset.offset,
        transform: "translateY(-50%)",
      };
    }
  };

  const tooltipContent = (isVisible || alwaysVisible) && (
    <div
      ref={tooltipRef}
      data-testid="tooltip-portal"
      className="fixed z-[9999]"
      style={{
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        minWidth: "200px",
        maxWidth: "350px",
        filter:
          "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.06))",
      }}
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
    >
      {/* Tooltip content */}
      <div
        className="text-gray-800 text-sm rounded-2xl px-5 py-3 border border-gray-200"
        style={{ backgroundColor: "#dee7ff" }}
      >
        {typeof text === "string" && /<\/?[a-z][\s\S]*>/i.test(text) ? (
          <span dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          text
        )}
      </div>

      {/* Arrow/Triangle */}
      <div
        className={`absolute ${getArrowClasses()}`}
        style={getArrowStyle()}
      />
    </div>
  );

  return (
    <>
      <div className={`inline-block ${className}`}>
        <div
          ref={triggerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          onTouchEnd={handleTouchEnd}
          className={`cursor-pointer items-center ${disablePad ? "" : "p-[0.3125rem]"} ${subContainerClassName}`}
        >
          {children}
        </div>
      </div>

      {tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  );
};

export default Tooltip;
